import { View } from "@westpierstudio/luma/mvc";
import { GameServices } from "game/GameServices";
import { CoreSymbolID } from "servertypes/SharedTypes";
import { Signal } from "type-signals";
import { addToSpineSlot, setSpineSkins } from "utils/Misc";
import { Spine } from "pixi-spine";
import { Emitter } from "pixi-particles";
import { Text } from "pixi.js";
import { BoundedContainer } from "utils/BoundedContainer";

type SpineText = {
    counter: Text,
    games: Text,
    win: Text
}

class MultiplierPanelSignals {
    showSymbol = new Signal();
    idlePulseComplete = new Signal();
}

export class MultiplierPanel extends View {
    private spec: Record<string, any>;
    private spine: Spine;
    private spineText: SpineText;
    private currentSkins = {panel: ``, multi: ``, symbol: ``};
    private baseCollectEmitter: Emitter;
    private wheelPrizeCollectEmitter: Emitter;
    private multiChangeEmitter: Emitter;
    signals = new MultiplierPanelSignals();

    constructor(services: GameServices, width: number) {
        super(services);

        const {factory, resources} = this.services;
        factory.applySpec(this, {x: 0, y: 0, pivot: {x: width/2, y: 0}});

        this.spec = resources.getSpec(`multiplierpanels`);
        this.spine = factory.createSpine(this.spec.multiplierPanel, this);
        this.spineText = this.setupText();

        const particleSpecs = resources.getSpec(`particles`);
        factory.createParticleEmitter(particleSpecs.multiplierPanelIdle, this);
        this.baseCollectEmitter = factory.createParticleEmitter(particleSpecs.multiplierPanelBaseCollect, this);
        this.wheelPrizeCollectEmitter = factory.createParticleEmitter(particleSpecs.multiplierPanelWheelPrizeCollect, this);
        this.multiChangeEmitter = factory.createParticleEmitter(particleSpecs.multiplierPanelMultiChange, this);

        this.visible = false;
    }

    set(symbol: CoreSymbolID, multiplier: number, counter: number, multiplierChange: boolean) {
        this.setPanelState(symbol, multiplier, counter, true, multiplierChange);
    }

    show(symbol: CoreSymbolID, multiplier: number, counter: number, showSymbol=false, wheelPrizeCollection=false): void {

        this.setPanelState(symbol, multiplier, counter, showSymbol, false);

        if (!showSymbol) {
            this.currentSkins.symbol = `symbol-` + symbol;
        }

        const anim = (symbol[0] === `M`) ? `intro-left` : `intro-right`;

        this.spine.state.setAnimation(0, anim, false);

        this.spine.state.addListener({ event : (_entry: any, event: any) => {

            if (event.data.name === `symbol`) {

                this.signals.showSymbol.dispatch();
                this.spine.state.clearListeners();

                if (wheelPrizeCollection) {
                    this.wheelPrizeCollectEmitter.emit = true;
                } else {
                    this.baseCollectEmitter.emit = true;
                }
            }
        }});
    }

    showSymbol(): void {

        const skins = Object.values(this.currentSkins).filter(item => item.length > 0);
        setSpineSkins(this.spine, skins);
    }

    hide(): void {

        this.spine.state.setAnimation(0, `exit`, false);
        this.spine.state.addListener({ complete: () => {

            this.currentSkins.symbol = ``;
            this.visible = false;
            this.spine.state.clearListeners();
        }});
    }

    idlePulse(): void {

        this.spine.state.clearListeners();

        this.spine.state.setAnimation(0, `idle`, false);
        this.spine.state.addListener({ complete: () => {

            this.signals.idlePulseComplete.dispatch();
            this.spine.state.clearListeners();
        }});
    }

    clearIdlePulse(): void {

        this.signals.idlePulseComplete.detachAll();

        this.spine.state.clearListeners();
        this.spine.state.clearTracks();
        this.spine.skeleton.setSlotsToSetupPose();
        this.spine.skeleton.setBonesToSetupPose();
    }

    override reset(): void {

        this.clearIdlePulse();

        this.currentSkins = {panel: ``, multi: ``, symbol: ``};
        this.visible = false;

        this.baseCollectEmitter.emit = false;
        this.wheelPrizeCollectEmitter.emit = false;
        this.multiChangeEmitter.emit = false;

        this.baseCollectEmitter.cleanup();
        this.wheelPrizeCollectEmitter.cleanup();
        this.multiChangeEmitter.cleanup();
    }

    private setupText(): SpineText {

        const counter = this.services.factory.createText(``, this.spec.multiPanelCounter);

        const gamesBounds = new BoundedContainer(this.spec.multiPanelGamesBounds);
        this.services.factory.applySpec(gamesBounds, this.spec.multiPanelGamesBounds);
        const games = this.services.factory.createText(
            this.services.resources.getMessage(`multiPanelGames`),
            this.spec.multiPanelGames,
            gamesBounds
        );
        gamesBounds.update();

        const winBounds = new BoundedContainer(this.spec.multiPanelWinBounds);
        this.services.factory.applySpec(winBounds, this.spec.multiPanelWinBounds);
        const win = this.services.factory.createText(
            this.services.resources.getMessage(`multiPanelWin`),
            this.spec.multiPanelWin,
            winBounds
        );
        winBounds.update();

        addToSpineSlot(this.spine, counter, `dynamic-number`);
        addToSpineSlot(this.spine, gamesBounds, `dynamic-games`);
        addToSpineSlot(this.spine, winBounds, `dynamic-win`);

        return {
            counter,
            games,
            win
        };
    }

    private setPanelState(symbol: CoreSymbolID, multiplier: number, counter: number, showSymbol: boolean,
        multiplierChange: boolean): void {

        if (showSymbol) {
            this.currentSkins.symbol = `symbol-` + symbol;
        }

        this.currentSkins.multi = `multiplier-x${multiplier}`;

        if (multiplierChange) {
            this.multiChangeEmitter.playOnce();
        }

        this.currentSkins.panel = `panel-2`;

        this.spineText.counter.text = counter;
        this.spineText.counter.visible = true;
        this.spineText.games.visible = true;

        this.spineText.games.text = this.services.resources.getMessage(counter === 1 ? `multiPanelGame` : `multiPanelGames`);

        const skins = Object.values(this.currentSkins).filter(item => item.length > 0);
        setSpineSkins(this.spine, skins);

        this.visible = true;
    }
}